import React from 'react';

const CheckCircleIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const SparklesIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
);

const LightningIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
);

const ChartIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
);

export const AboutPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-[var(--surface-1)]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 animate-fade-in">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[var(--text-1)] font-serif mb-6">
                            About CompareLens
                        </h1>
                        <p className="text-lg sm:text-xl text-[var(--text-2)] max-w-2xl mx-auto leading-relaxed">
                            Revolutionizing laptop discovery with the power of artificial intelligence
                        </p>
                    </div>

                    <div className="bg-[var(--surface-2)] rounded-3xl shadow-xl border border-[var(--surface-4)] p-8 sm:p-12 mb-12">
                        <h2 className="text-2xl sm:text-3xl font-bold text-[var(--text-1)] font-serif mb-6">
                            Our Mission
                        </h2>
                        <p className="text-[var(--text-2)] leading-relaxed text-base sm:text-lg mb-6">
                            At CompareLens, we believe that finding the perfect laptop should be simple, fast, and personalized.
                            With thousands of laptop models flooding the market every year, making an informed decision has
                            never been more challenging.
                        </p>
                        <p className="text-[var(--text-2)] leading-relaxed text-base sm:text-lg">
                            Our mission is to cut through the noise and deliver instant, data-driven recommendations
                            tailored to your unique needs. We leverage cutting-edge AI technology to analyze specs,
                            reviews, benchmarks, and real-world performance data, so you can make confident decisions.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12">
                        <div className="bg-[var(--surface-2)] rounded-2xl shadow-lg border border-[var(--surface-4)] p-6 sm:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-[var(--brand)]/10 rounded-xl flex items-center justify-center">
                                    <SparklesIcon />
                                </div>
                                <h3 className="text-xl font-bold text-[var(--text-1)]">AI-Powered</h3>
                            </div>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                Our advanced AI engine processes millions of data points to deliver accurate,
                                personalized recommendations in seconds.
                            </p>
                        </div>

                        <div className="bg-[var(--surface-2)] rounded-2xl shadow-lg border border-[var(--surface-4)] p-6 sm:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-[var(--brand)]/10 rounded-xl flex items-center justify-center">
                                    <LightningIcon />
                                </div>
                                <h3 className="text-xl font-bold text-[var(--text-1)]">Real-Time Data</h3>
                            </div>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                We continuously scan the web for the latest specs, prices, and reviews
                                to ensure you get the most current information.
                            </p>
                        </div>

                        <div className="bg-[var(--surface-2)] rounded-2xl shadow-lg border border-[var(--surface-4)] p-6 sm:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-[var(--brand)]/10 rounded-xl flex items-center justify-center">
                                    <ChartIcon />
                                </div>
                                <h3 className="text-xl font-bold text-[var(--text-1)]">Deep Analysis</h3>
                            </div>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                Compare benchmarks, performance metrics, and specs side-by-side to
                                understand exactly what you're getting.
                            </p>
                        </div>

                        <div className="bg-[var(--surface-2)] rounded-2xl shadow-lg border border-[var(--surface-4)] p-6 sm:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-[var(--brand)]/10 rounded-xl flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[var(--text-1)]">User-Centric</h3>
                            </div>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                Whether you're a student, gamer, or professional, our platform adapts
                                to your specific requirements and budget.
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-[var(--brand)]/5 to-[var(--accent)]/5 rounded-3xl border border-[var(--brand)]/20 p-8 sm:p-12">
                        <h2 className="text-2xl sm:text-3xl font-bold text-[var(--text-1)] font-serif mb-6">
                            Why Choose CompareLens?
                        </h2>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <CheckCircleIcon />
                                <span className="text-[var(--text-2)] leading-relaxed">
                                    <strong className="text-[var(--text-1)]">Save Time:</strong> Get expert recommendations in seconds instead of hours of research
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircleIcon />
                                <span className="text-[var(--text-2)] leading-relaxed">
                                    <strong className="text-[var(--text-1)]">Make Informed Decisions:</strong> Access comprehensive comparisons and real-world performance data
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircleIcon />
                                <span className="text-[var(--text-2)] leading-relaxed">
                                    <strong className="text-[var(--text-1)]">Stay Updated:</strong> Our AI continuously learns and updates with the latest market trends
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircleIcon />
                                <span className="text-[var(--text-2)] leading-relaxed">
                                    <strong className="text-[var(--text-1)]">Unbiased Results:</strong> Pure data-driven recommendations with no hidden agendas
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div className="text-center mt-12 sm:mt-16">
                        <p className="text-lg text-[var(--text-2)] mb-6">
                            Ready to find your perfect laptop?
                        </p>
                        <a
                            href="/"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-[var(--brand)] text-[var(--brand-text)] font-semibold rounded-xl hover:bg-[var(--brand-hover)] transition-all shadow-lg hover:shadow-xl"
                        >
                            Get Started
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
